import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import Color from 'color';
import {
  COLOR,
  FONT_SIZE,
  FONT_WEIGHT,
  RADIUS,
  SPACING,
} from 'constants/design-system';
import { DateTime } from 'luxon';

import colors from 'config/colors';
import { RemakeOrderDescription } from 'modules/OverviewScene/components';
import { useAppSelector } from 'store/hooks';
import { NotificationsSelectors } from 'store/Notifications';
import { OrderInternalStatuses } from 'store/Orders/enums';
import { getOrderInternalStatus } from 'store/Orders/utils';
import { SettingSelectors } from 'store/Settings';
import { UserSelectors } from 'store/User';
import { getIconNameForOrderKind, useFormatDateTime } from 'util/helpers';
import { useCurrencyString } from 'util/helpers/currency';
import { useSumOrderItems } from 'util/hooks';
import { BaseCard } from 'components/BaseCard';
import { BaseIcon } from 'components/BaseIcon';
import { BasePressable } from 'components/BasePressable';
import { BaseText } from 'components/BaseText';
import { CountDownLabel } from 'components/CountDownLabel';
import {
  CancelledOrderBadge,
  DeliveryStatusBadge,
  OrderStartPreparationStatusBadge,
} from 'components/StatusBadge';

import { useOrderCardDispaySettings } from './useOrderCardDisplaySettings';
import type { BaseCardProps } from 'components/BaseCard';
import type { BasePressableProps } from 'components/BasePressable';
import type { GestureResponderEvent } from 'react-native';
import type { Order, OrderItem } from 'store/Orders/types';

export type OrderCardProps = {
  order: Order;
  items: OrderItem[];
  isSelected?: boolean;
  onPress?: (event: GestureResponderEvent, orderUuid: string) => void;
  style?: BasePressableProps['style'];
  cardStyle?: BaseCardProps['style'];
};

export const OrderCard = (props: OrderCardProps) => {
  const { order, items, isSelected, onPress, style, cardStyle } = props;

  const featureFlags = useAppSelector(UserSelectors.selectFeatureFlags);

  const {
    shouldDisplayCustomerAddress,
    shouldDisplayCustomerName,
    shouldDisplayCustomerPhoneNumber,
    shouldDisplayNumberOfItems,
    shouldDisplayTotalOrderValue,
  } = useOrderCardDispaySettings();

  const orderInternalStatus = getOrderInternalStatus(order, items);

  const formatDateTime = useFormatDateTime();

  const itemCount = useSumOrderItems(items);

  const { t } = useTranslation();

  const hasUnacceptedNotifications = useAppSelector((state) =>
    NotificationsSelectors.selectOrderHasUnattended(state, order.uuid),
  );

  const backgroundColor = isSelected
    ? colors.darkGray
    : hasUnacceptedNotifications
      ? Color(colors.ui.notifications.unread).darken(0.3).fade(0.1).hexa()
      : colors.white;

  const textColor = isSelected ? colors.white : colors.darkGray;

  const getCurrencyString = useCurrencyString();

  return (
    <BasePressable
      style={style}
      onPress={(e) => {
        onPress?.(e, order.uuid);
      }}
    >
      <BaseCard
        style={[
          { flexDirection: 'row', padding: 0, overflow: 'hidden' },
          isSelected && {
            borderWidth: 2,
            borderColor: COLOR.system.orderKinds[order.kind],
          },
          cardStyle,
        ]}
      >
        <View
          style={{
            backgroundColor: COLOR.theme.gray[900],
            padding: SPACING.theme[6],
            width: 64,
            alignItems: 'center',
          }}
        >
          <View
            style={{
              backgroundColor: COLOR.system.orderKinds[order.kind],
              borderColor: COLOR.theme.white,
              borderRadius: RADIUS.theme.md,
              borderWidth: 1,
              alignItems: 'center',
              justifyContent: 'center',
              height: 48,
              width: 48,
            }}
          >
            <BaseIcon
              color={COLOR.theme.white}
              name={getIconNameForOrderKind(order.kind)}
              variant="material-community"
              size={FONT_SIZE.theme['2xl']}
            />
          </View>
          {order.latest_pickup_time &&
            (
              [
                OrderInternalStatuses.NOT_BEING_PREPARED,
                OrderInternalStatuses.BEING_PREPARED,
              ] as unknown[]
            ).includes(orderInternalStatus) && (
              <CountDownLabel latestPickupTime={order.latest_pickup_time} />
            )}
        </View>
        <View
          style={{
            padding: SPACING.theme[4],
            gap: SPACING.theme[4],
            flex: 1,
            backgroundColor: backgroundColor,
            flexDirection: 'row',
          }}
        >
          <View style={{ flexShrink: 1, flexGrow: 1, gap: SPACING.theme[4] }}>
            <BaseText
              style={{ marginBottom: 'auto' }}
              color={textColor}
              fontWeight={FONT_WEIGHT.theme.bold}
            >
              #{order.description}
              {order.remake_for_order_description && (
                <RemakeOrderDescription
                  description={order.remake_for_order_description}
                  color={textColor}
                  style={{ marginLeft: 4, marginTop: 4 }}
                  prefix=""
                />
              )}
            </BaseText>

            <View
              style={{
                alignItems: 'flex-start',
              }}
            >
              {shouldDisplayCustomerName && (
                <View style={{ flexDirection: 'row', gap: SPACING.theme[4] }}>
                  <BaseIcon
                    variant="material-community"
                    name="account"
                    color={textColor}
                  />
                  <BaseText
                    fontWeight={FONT_WEIGHT.theme.medium}
                    fontSize={FONT_SIZE.theme.xs}
                    numberOfLines={1}
                    color={textColor}
                  >
                    {order.recipient.full_name}
                  </BaseText>
                </View>
              )}
              {shouldDisplayCustomerAddress &&
                order.dropoff_location?.line_1 && (
                  <View style={{ flexDirection: 'row', gap: SPACING.theme[4] }}>
                    <BaseIcon
                      variant="material"
                      name="home"
                      color={textColor}
                    />
                    <BaseText
                      fontWeight={FONT_WEIGHT.theme.medium}
                      fontSize={FONT_SIZE.theme.xs}
                      numberOfLines={2}
                      color={textColor}
                    >
                      {order.dropoff_location?.line_1}
                    </BaseText>
                  </View>
                )}
              {shouldDisplayCustomerPhoneNumber &&
                order.recipient.phone_number && (
                  <View style={{ flexDirection: 'row', gap: SPACING.theme[4] }}>
                    <BaseIcon
                      variant="material"
                      name="phone"
                      color={textColor}
                    />
                    <BaseText
                      fontWeight={FONT_WEIGHT.theme.medium}
                      fontSize={FONT_SIZE.theme.xs}
                      numberOfLines={2}
                      color={textColor}
                    >
                      {order.recipient.phone_number}
                    </BaseText>
                  </View>
                )}
              {/* //TODO: phone number */}
            </View>
          </View>
          <View style={{ width: 110, gap: SPACING.theme[4] }}>
            {featureFlags.kds_enabled_traffic_light_system &&
              orderInternalStatus ===
                OrderInternalStatuses.NOT_BEING_PREPARED && (
                <OrderStartPreparationStatusBadge
                  status={order.start_preparation_status}
                  // iconSize={FONT_SIZE.theme.lg}
                  // textSize={FONT_SIZE.theme.xs}
                  style={{
                    // NOTE: remove default margin
                    marginBottom: 0,
                  }}
                />
              )}
            {orderInternalStatus === OrderInternalStatuses.CANCELLED ? (
              <CancelledOrderBadge />
            ) : order.job &&
              orderInternalStatus === OrderInternalStatuses.PREPARED ? (
              <DeliveryStatusBadge
                status={order.job.delivery_status}
                geoVerified={order.job.geo_verified}
              />
            ) : null}

            {order.scheduled_for && (
              <View
                style={{
                  flexDirection: 'row',
                  gap: SPACING.theme[4],
                  alignItems: 'center',
                  backgroundColor: COLOR.theme.blue[400],
                  borderRadius: 4,
                  paddingVertical: 4,
                  paddingHorizontal: 2,
                  justifyContent: 'center',
                }}
              >
                <BaseIcon
                  variant="material-community"
                  name="calendar-clock"
                  color={COLOR.theme.white}
                />
                <BaseText
                  fontWeight={FONT_WEIGHT.theme.medium}
                  fontSize={FONT_SIZE.theme.xs}
                  numberOfLines={2}
                  color={COLOR.theme.white}
                >
                  {formatDateTime(order.scheduled_for, DateTime.TIME_SIMPLE)}
                </BaseText>
              </View>
            )}

            <View
              style={{
                borderWidth: 1,
                borderColor: textColor,
                borderRadius: RADIUS.theme.md,
                padding: SPACING.theme[4],
                alignItems: 'flex-end',
                marginTop: 'auto',
                // justifyContent: 'space-between',
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: SPACING.theme[4],
                }}
              >
                <BaseIcon
                  variant="material-community"
                  name={'at'}
                  color={textColor}
                  size={FONT_SIZE.theme.base}
                />
                <BaseText
                  fontWeight="bold"
                  textAlign="right"
                  color={textColor}
                  fontSize={FONT_SIZE.theme.xs}
                >
                  {formatDateTime(order.created_at, DateTime.TIME_SIMPLE)}
                </BaseText>
              </View>
              {shouldDisplayTotalOrderValue && (
                <BaseText
                  fontWeight="bold"
                  textAlign="right"
                  color={textColor}
                  fontSize={FONT_SIZE.theme.xs}
                >
                  {getCurrencyString(
                    order.financial_info.grand_total_including_tax,
                  )}
                </BaseText>
              )}
              {shouldDisplayNumberOfItems && (
                <BaseText
                  fontWeight="bold"
                  textAlign="right"
                  color={textColor}
                  fontSize={FONT_SIZE.theme.xs}
                >
                  {t('overview.drawer.orderItem.items', {
                    count: itemCount,
                  })}
                </BaseText>
              )}
            </View>
          </View>
        </View>
      </BaseCard>
    </BasePressable>
  );
};
